<template>
  <section class="columns mgt-20px">
    <div class="column is-narrow">
      <app-logo/>
    </div>
    <div class="column">
      <div class="card">
        <header class="header-card">
          <p>{{ $t('addEditFournisseurs.informationsGenerales.title')}}</p>
        </header>
        <div class="card-content">
          <div class="columns is-multiline">
            <div class="column is-3">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.code')">
                <b-input v-model="fournisseur.code" disabled></b-input>
              </b-field>
            </div>
            <div class="column is-3">
              <input-tier-nom-commercial
                v-model="fournisseur.nomCommercial"
                :label="labelNomCommercial"
                :tierId="fournisseur.id"
                :query="fournisseurQuery"
                path="c.achat.fournisseurs">
              </input-tier-nom-commercial>
            </div>
            <div class="column is-3">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.siret')">
                <b-input v-model="fournisseur.siret"></b-input>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.apeNaf')">
                <b-input v-model="fournisseur.ape"></b-input>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                :label="$t('addEditFournisseurs.informationsGenerales.tvaIntracommunautaire')">
                <b-input v-model="fournisseur.tvaIntracommunautaire"></b-input>
              </b-field>
            </div>
            <div class="column is-3" v-if="!isService">
              <b-field
                :label="$t('addEditFournisseurs.informationsGenerales.role')"
                class="obligatoire">
                <qd-taginput
                  v-model="sousTypes"
                  @typing="getFilteredSousTypeWritting"
                  @input="getTypeTags"
                  @remove="removeTag"
                  :data="getDataFilter"
                  :allow-new="false"
                  field="libellei18n"
                  ref="sousTypesTagInput"
                  type="is-primary"
                  open-on-focus
                  autocomplete
                  expanded>
                  <template #selected="props">
                    <b-tag
                      v-for="(tag, index) in props.tags" :key="index"
                      @close="$refs.sousTypesTagInput.removeTag(index, $event)"
                      :closable="tag.isDeletable"
                      type="is-primary">
                      {{ tag.libellei18n }}
                    </b-tag>
                  </template>
                </qd-taginput>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.applicationTva')">
                <b-select v-model="fournisseur.applicationTva" expanded>
                  <option
                    v-for="item in constantes.LISTE_APPLICATION_TVA"
                    :value="item.code"
                    :key="item.code">
                    {{ $t(item.libelle) }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3" v-if="isFactor">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.numeroCedant')">
                <b-input v-model="fournisseur.numeroCedant"></b-input>
              </b-field>
            </div>
            <div class="column is-3" v-if="isFactor">
              <b-field :label="$t('addEditFournisseurs.informationsGenerales.numeroFichier')">
                <qd-numberinput v-model="fournisseur.numeroFichier" :controls="false">
                </qd-numberinput>
              </b-field>
            </div>
            <div class="column is-narrow"  v-if="!isService">
              <b-field class="has-text-default bfieldWhitoutLabel">
                <qd-checkbox v-model="fournisseur.interne">
                  {{ $t('addEditFournisseurs.informationsGenerales.interne')}}
                </qd-checkbox>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import AppLogo from '@/components/general/Logo';
import InputTierNomCommercial from '@/components/general/input/tierNomCommercialInput/tierNomCommercialInput';
import traductionLibelleEtat from '@/services/devisCommandes/traductionLibelleEtat';
import {
  LISTE_APPLICATION_TVA,
  TIERS_TYPE_FOURNISSEUR,
  TIERS_SOUS_TYPE_FACTOR,
  LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES,
  TIERS_SOUS_TYPE_SERVICE,
  LISTE_SOUS_TYPE_FOURNISSEUR_ALL,
} from '@/configuration/tiers/tiersConf';

const Societe = createNamespacedHelpers('societe');

export default {
  name: 'InformationsGeneral',
  props: {
    fournisseur: {
      type: Object,
      required: true,
    },
  },
  components: {
    AppLogo,
    InputTierNomCommercial,
  },
  data() {
    return {
      filteredTags: traductionLibelleEtat(this.$i18n, LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES),
      constantes: {
        LISTE_APPLICATION_TVA,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    fournisseurQuery() {
      return {
        nomsCommercialOuNomsJuridique: [this.fournisseur.nomCommercial],
        societeId: this.societeActiveId,
        type: TIERS_TYPE_FOURNISSEUR,
        sousType: LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES.map((el) => el.code),
      };
    },
    getDataFilter() {
      return this.filteredTags
        .filter((type) => !this.fournisseur.sousTypes.includes(type.code));
    },
    isFactor() {
      return this.isType(TIERS_SOUS_TYPE_FACTOR);
    },
    isService() {
      return this.isType(TIERS_SOUS_TYPE_SERVICE);
    },
    labelNomCommercial() {
      if (this.isService) return this.$t('addEditFournisseurs.informationsGenerales.nomService');
      return this.$t('addEditFournisseurs.informationsGenerales.nomCommercial');
    },
    sousTypes: {
      get() {
        const types = LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES
          .filter((t) => this.fournisseur.sousTypes.includes(t.code))
          .map((t) => ({ ...t, isDeletable: true }));
        if (types.length !== this.fournisseur.sousTypes.length) {
          const missingTypes = this.fournisseur.sousTypes
            .filter((t) => !types.map((el) => el.code).includes(t))
            .map((t) => ({
              ...LISTE_SOUS_TYPE_FOURNISSEUR_ALL.find((el) => el.code === t),
              isDeletable: false,
            }));
          types.unshift(...missingTypes);
        }
        return traductionLibelleEtat(this.$i18n, types);
      },
      set(value) {
        this.fournisseur.sousTypes = value.map((t) => t.code);
      },
    },
  },
  methods: {
    getFilteredSousTypeWritting(text) {
      this.filteredTags = LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES
        .filter((type) => {
          return this.fournisseur.sousTypes.some((sousType) => {
            return sousType !== type.code;
          });
        })
        .filter((option) => {
          return option.libellei18n.toString().toLowerCase().indexOf(text.toLowerCase()) >= 0;
        });
    },
    getTypeTags() {
      this.filteredTags = LISTE_SOUS_TYPE_FOURNISSEURS_ACHAT_PAGES.filter((option) => {
        return !this.fournisseur.sousTypes.map(c => c.code).includes(option.code);
      });
    },
    isType(type) {
      return this.fournisseur.sousTypes.some((el) => el === type);
    },
    removeTag(tag) {
      const copy = cloneDeep(this.sousTypes);
      if (!tag.isDeletable) {
        copy.unshift(tag);
        this.sousTypes = copy;
      }
    },
  },
};
</script>
